<template>
  <div class="generation studio-layout">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>批量生成配置</span>
          <div class="header-actions">
            <el-select
              v-model="selectedHistoryId"
              placeholder="载入历史配置"
              clearable
              filterable
              style="width: 220px; margin-right: 8px"
              @change="applyFormHistory"
            >
              <el-option
                v-for="h in formHistoryList"
                :key="h.id"
                :label="historyLabel(h)"
                :value="h.id"
              />
            </el-select>
            <el-button size="small" :disabled="!form.name" @click="saveFormHistoryManual">保存当前为历史</el-button>
            <el-tag type="info" size="small">{{ allArticleTypes.length }} 种文章类型</el-tag>
          </div>
        </div>
      </template>

      <el-form :model="form" label-width="120px">
        <el-form-item label="市场">
          <el-radio-group v-model="form.market" size="default" @change="onMarketChange">
            <el-radio-button label="domestic">国内</el-radio-button>
            <el-radio-button label="overseas">海外（TabStory / 英文）</el-radio-button>
          </el-radio-group>
          <span class="hint-text">国内走中文平台与 T-13~T-19；海外走英文平台与 OT-01~OT-07，话题池与全局提示词分区独立。</span>
        </el-form-item>

        <el-form-item label="方向">
          <el-select
            v-model="form.direction"
            filterable
            allow-create
            default-first-option
            clearable
            placeholder="选择或输入本批内容方向"
            style="width: 100%"
            @change="onTopicSeedChange"
          >
            <el-option
              v-for="g in genreOptions"
              :key="g.code"
              :label="`${g.name || g.code}${g.description ? ' · ' + g.description : ''}`"
              :value="g.name || g.code"
            />
          </el-select>
          <p class="field-tip">方向会作为主题引导词传给话题池生成，例如：爽文转折、角色关系、避坑教程、热点盘点等。</p>
        </el-form-item>

        <el-form-item label="IP / 作品">
          <el-select
            v-model="form.ip_code"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="选择 IP，或在下方补充自定义 IP"
            style="width: 100%"
            :remote-method="searchIps"
            :loading="ipLoading"
            @change="onIpSelect"
          >
            <el-option
              v-for="ip in ipOptions"
              :key="ip.code"
              :label="`${ip.name || ip.code}${ip.category ? ' · ' + ip.category : ''}${ip.heat ? ' · ' + ip.heat : ''}`"
              :value="ip.code"
            />
          </el-select>
          <el-input
            v-model="form.custom_ip"
            class="inline-extra-input"
            clearable
            placeholder="可选：自定义 IP / 作品 / 角色 / 品牌，填写后优先使用"
            @input="onTopicSeedChange"
          />
        </el-form-item>

        <el-form-item label="任务名称">
          <el-input v-model="form.name" :placeholder="autoNamePreview" clearable />
        </el-form-item>

        <el-form-item label="目标平台">
          <el-checkbox-group v-model="form.platforms">
            <el-checkbox v-for="opt in platformOptions" :key="opt.id" :label="opt.id">{{ opt.label }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <el-form-item label="文章类型">
          <el-select
            v-model="form.article_types"
            multiple
            placeholder="选择文章类型（每种类型自带专属系统提示词）"
            style="width: 100%"
            :max-collapse-tags="4"
            collapse-tags
            collapse-tags-tooltip
            @change="onTopicSeedChange"
          >
            <el-option v-for="t in articleTypeOptions" :key="t.id" :value="t.id" :label="`${t.id} ${t.name}`">
              <div class="type-option">
                <span class="type-id">{{ t.id }}</span>
                <span class="type-name">{{ t.name }}</span>
                <span class="type-tone">{{ t.tone }}</span>
              </div>
            </el-option>
          </el-select>
          <div v-if="form.article_types.length > 0" class="suffix-preview">
            <div v-for="tid in form.article_types" :key="tid" class="type-detail-item">
              <el-tag size="small" type="primary">{{ tid }}</el-tag>
              <span class="type-detail-name">{{ getTypeName(tid) }}</span>
              <el-tag size="small" type="warning">自带专属提示词</el-tag>
              <span class="type-detail-suffix">搜索词：<code>{{ getTypeSuffix(tid) }}</code></span>
            </div>
          </div>
          <p class="type-hint">文章类型决定正文结构和话题池生成策略；方向与 IP 会作为本批话题的定向约束。</p>
        </el-form-item>

        <el-form-item label="话题数量">
          <el-slider v-model="form.count" :min="1" :max="200" show-input :input-size="'small'" style="width:80%" @change="onTopicSeedChange" />
          <span class="count-hint">将先生成 {{ form.count }} 条话题，确认后生成正文</span>
        </el-form-item>

        <el-form-item label="话题池生成">
          <div class="topic-actions">
            <el-button type="primary" :loading="topicGeneration.running" @click="generateTopicPoolForBatch">生成本批话题池</el-button>
            <el-button :disabled="topicGeneration.running" @click="addTopicDraft">新增话题</el-button>
            <el-button :disabled="topicGeneration.running || !topicDrafts.length" @click="clearTopicDrafts">清空话题</el-button>
            <el-switch v-model="form.search_materials" active-text="自动搜索素材" />
            <el-input-number v-if="form.search_materials" v-model="form.material_budget" :min="1" :max="10" size="small" />
          </div>
          <div v-if="topicGeneration.jobId" class="topic-progress">
            <el-progress
              :percentage="topicGeneration.progressPct"
              :status="topicGeneration.jobStatus === 'completed' ? 'success' : topicGeneration.jobStatus === 'failed' ? 'exception' : undefined"
            />
            <p class="topic-progress-msg">{{ topicGeneration.progressMsg }}</p>
          </div>
          <p class="field-tip">流程：选择方向和 IP → 生成话题池 → 在下方编辑/增减话题 → 确认后创建正文生成任务。</p>
        </el-form-item>

        <el-form-item label="生成并发">
          <el-input-number v-model="form.concurrency" :min="1" :max="100" />
          <span class="hint-text">正文并发生成上限；过高可能触发 API 限流</span>
        </el-form-item>

        <el-form-item>
          <el-button type="success" size="large" :loading="loading" :disabled="topicGeneration.running" @click="submitTask">
            确认话题并生成正文
          </el-button>
          <el-button size="large" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="preview-card" v-if="topicDrafts.length > 0">
      <template #header>
        <div class="card-header">
          <div class="preview-header-text">
            <span>本批话题池（{{ topicDrafts.length }} 条，可直接编辑）</span>
            <p class="preview-note">每行话题会绑定文章类型并轮流分配到所选平台；修改话题文本后，会按修改后的内容生成正文。</p>
          </div>
          <el-tag type="success">待生成 {{ topicDrafts.length }} 篇正文</el-tag>
        </div>
      </template>
      <el-table :data="topicDrafts" size="small" stripe row-key="_key">
        <el-table-column label="#" type="index" width="50" />
        <el-table-column label="文章类型" width="150">
          <template #default="{ row }">
            <el-select v-model="row.article_type" size="small" style="width: 130px">
              <el-option v-for="t in articleTypeOptions" :key="t.id" :label="`${t.id} ${t.name}`" :value="t.id" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="话题内容" min-width="300">
          <template #default="{ row }">
            <el-input v-model="row.topic_text" type="textarea" :rows="2" placeholder="请输入本篇正文的话题/角度" />
          </template>
        </el-table-column>
        <el-table-column label="搜索词" min-width="220">
          <template #default="{ row }">
            <el-input v-model="row.search_query" size="small" placeholder="可选：素材搜索词" />
          </template>
        </el-table-column>
        <el-table-column label="素材" width="90" align="center">
          <template #default="{ row }">
            <el-tag size="small" :type="normalizeMaterials(row.materials).length ? 'success' : 'info'">
              {{ normalizeMaterials(row.materials).length }} 条
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="110">
          <template #default="{ $index }">
            <el-tag size="small" type="info">{{ platformName(platformForIndex($index)) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right">
          <template #default="{ $index }">
            <el-button link size="small" type="primary" @click="duplicateTopicDraft($index)">复制</el-button>
            <el-button link size="small" type="danger" @click="removeTopicDraft($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import { formHistoryApi, topicPoolApi } from '../api'

export default {
  name: 'Generation',
  setup() {
    const loading = ref(false)
    const configData = ref({ topics: {}, article_types: {}, matrix: {} })
    const formHistoryList = ref([])
    const selectedHistoryId = ref(null)
    const genreOptions = ref([])
    const ipOptions = ref([])
    const ipLoading = ref(false)
    const topicDrafts = ref([])

    const form = reactive({
      name: '',
      count: 10,
      platforms: ['baijiahao'],
      article_types: ['T-13'],
      concurrency: 100,
      market: 'domestic',
      direction: '',
      ip_code: '',
      ip_name: '',
      custom_ip: '',
      search_materials: true,
      material_budget: 5,
    })
    const lastAutoName = ref('')

    const topicGeneration = reactive({
      running: false,
      jobId: null,
      jobStatus: '',
      progressPct: 0,
      progressMsg: '',
      pollTimer: null,
    })

    const loadConfig = async () => {
      try {
        const res = await axios.get('/api/config')
        configData.value = res.data
      } catch (e) {
        ElMessage.error('加载配置失败: ' + (e.response?.data?.detail || e.message))
      }
    }

    const loadFormHistory = async () => {
      try {
        const res = await formHistoryApi.list({ limit: 50 })
        formHistoryList.value = res.items || []
      } catch (e) {}
    }

    const loadGenreOptions = async () => {
      try {
        const res = await axios.get('/api/genres')
        genreOptions.value = res.data.items || []
      } catch (e) {
        genreOptions.value = []
      }
    }

    const loadIpOptions = async (search = '') => {
      ipLoading.value = true
      try {
        const params = search ? { search } : { min_heat: 'A' }
        const res = await axios.get('/api/ips', { params })
        ipOptions.value = res.data.items || []
      } catch (e) {
        ipOptions.value = []
      } finally {
        ipLoading.value = false
      }
    }

    const searchIps = (query) => {
      loadIpOptions(query || '')
    }

    const historyLabel = (h) => {
      const t = h.created_at ? new Date(h.created_at).toLocaleString('zh-CN') : ''
      const title = h.label || (h.form && h.form.name) || '未命名'
      return `${title} · ${t}`
    }

    const applyFormHistory = (id) => {
      if (!id) return
      const row = formHistoryList.value.find((x) => x.id === id)
      if (!row || !row.form) return
      const f = row.form
      form.name = f.name ?? form.name
      form.count = f.count ?? 10
      form.market = f.market ?? 'domestic'
      form.direction = f.direction ?? ''
      form.ip_code = f.ip_code ?? ''
      form.ip_name = f.ip_name ?? ''
      form.custom_ip = f.custom_ip ?? ''
      form.search_materials = f.search_materials ?? true
      form.material_budget = f.material_budget ?? 5
      const allowed = new Set(platformOptions.value.map((o) => o.id))
      form.platforms = Array.isArray(f.platforms) ? f.platforms.filter((p) => allowed.has(p)) : form.platforms
      if (!form.platforms.length) form.platforms = form.market === 'overseas' ? ['reddit'] : ['baijiahao']
      form.article_types = Array.isArray(f.article_types) ? [...f.article_types] : form.article_types
      form.concurrency = f.concurrency ?? 100
      topicDrafts.value = []
      ElMessage.success('已载入历史配置')
    }

    const formSnapshot = () => ({
      name: form.name,
      count: form.count,
      platforms: [...form.platforms],
      article_types: [...form.article_types],
      concurrency: form.concurrency,
      market: form.market,
      direction: form.direction,
      ip_code: form.ip_code,
      ip_name: form.ip_name,
      custom_ip: form.custom_ip,
      search_materials: form.search_materials,
      material_budget: form.material_budget,
    })

    const saveFormHistoryManual = async () => {
      if (!form.name) return
      try {
        await formHistoryApi.save({ label: form.name, form: formSnapshot() })
        ElMessage.success('已保存到配置历史')
        await loadFormHistory()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      }
    }

    watch(
      () => form.platforms,
      () => applyAutoTaskName(),
      { deep: true }
    )

    const platformOptions = computed(() => {
      if (form.market === 'overseas') {
        const po = configData.value.platforms_overseas || {}
        return Object.entries(po).map(([id, cfg]) => ({ id, label: cfg?.name || id }))
      }
      return [
        { id: 'xiaohongshu', label: '小红书' },
        { id: 'toutiao', label: '头条号' },
        { id: 'zhihu', label: '知乎' },
        { id: 'baijiahao', label: '百家号' },
      ]
    })

    const onMarketChange = () => {
      if (form.market === 'overseas') {
        form.platforms = ['reddit']
        form.article_types = ['OT-01']
      } else {
        form.platforms = ['baijiahao']
        form.article_types = ['T-13']
      }
      form.ip_code = ''
      form.ip_name = ''
      topicDrafts.value = []
      applyAutoTaskName(true)
    }

    const mergedArticleTypes = computed(() => ({
      ...(configData.value.article_types || {}),
      ...(configData.value.article_types_overseas || {}),
    }))

    const articleTypeOptions = computed(() => {
      const src = form.market === 'overseas'
        ? configData.value.article_types_overseas || {}
        : configData.value.article_types || {}
      return Object.entries(src).map(([id, info]) => ({
        id,
        name: info.name || id,
        tone: info.tone || '',
        search_query_suffix: info.search_query_suffix || '',
      }))
    })

    const getTypeSuffix = (tid) => mergedArticleTypes.value[tid]?.search_query_suffix || ''
    const getTypeName = (tid) => mergedArticleTypes.value[tid]?.name || tid

    const platformName = (p) => {
      if (form.market === 'overseas') {
        const po = configData.value.platforms_overseas || {}
        return po[p]?.name || p
      }
      const m = { xiaohongshu: '小红书', toutiao: '头条', zhihu: '知乎', baijiahao: '百家号', lofter: 'LOFTER' }
      return m[p] || p
    }

    const formatNow = () => {
      const d = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`
    }

    const autoNamePreview = computed(() => {
      const names = (form.platforms || []).map(platformName)
      const platformPart = names.length ? names.join('+') : '未选平台'
      return `${platformPart}_${formatNow()}`
    })

    const applyAutoTaskName = (force = false) => {
      const next = autoNamePreview.value
      const canReplace = !form.name || form.name === lastAutoName.value
      if (force || canReplace) {
        form.name = next
        lastAutoName.value = next
      }
    }

    const onIpSelect = (code) => {
      const ip = ipOptions.value.find((x) => x.code === code)
      form.ip_name = ip?.name || ''
      onTopicSeedChange()
    }

    const onTopicSeedChange = () => {
      if (topicDrafts.value.length) {
        topicDrafts.value = []
      }
    }

    const currentThemeIp = computed(() => (form.custom_ip || form.ip_name || '').trim())
    const currentDirection = computed(() => (form.direction || '').trim())

    const resetTopicGenerationState = () => {
      if (topicGeneration.pollTimer) clearTimeout(topicGeneration.pollTimer)
      topicGeneration.running = false
      topicGeneration.jobId = null
      topicGeneration.jobStatus = ''
      topicGeneration.progressPct = 0
      topicGeneration.progressMsg = ''
      topicGeneration.pollTimer = null
    }

    const normalizeMaterials = (materials) => {
      if (typeof materials === 'string') {
        try { return JSON.parse(materials) || [] } catch { return [] }
      }
      return Array.isArray(materials) ? materials : []
    }

    const normalizeTopicItem = (item, index = 0) => ({
      _key: item._key || `${item.id || 'local'}-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      id: item.id,
      article_type: item.article_type || form.article_types[index % Math.max(form.article_types.length, 1)] || (form.market === 'overseas' ? 'OT-01' : 'T-13'),
      topic_text: item.topic_text || '',
      search_query: item.search_query || '',
      materials: normalizeMaterials(item.materials),
      source: item.source || (item.id ? 'auto' : 'manual'),
    })

    const fetchRecentGeneratedTopics = async () => {
      const res = await topicPoolApi.getList({
        market: form.market,
        status: 'unused',
        limit: form.count,
      })
      return res.items || []
    }

    const generateTopicPoolForBatch = async () => {
      if (!form.article_types.length) { ElMessage.warning('请至少选择一种文章类型'); return }
      if (!currentDirection.value && !currentThemeIp.value) { ElMessage.warning('请先选择方向或 IP'); return }
      resetTopicGenerationState()
      topicGeneration.running = true
      topicGeneration.progressMsg = '正在启动话题池生成...'
      try {
        const res = await topicPoolApi.generate({
          article_types: form.article_types,
          total_count: form.count,
          search_materials: form.search_materials,
          material_budget: form.material_budget,
          market: form.market,
          theme_ip: currentThemeIp.value,
          theme_guidance: currentDirection.value,
        })
        const jobId = res.job_id
        topicGeneration.jobId = jobId
        topicGeneration.progressMsg = '生成中...'
        const poll = async () => {
          try {
            const s = await topicPoolApi.getGenerateStatus(jobId)
            topicGeneration.jobStatus = s.status
            topicGeneration.progressMsg = s.message || ''
            const done = s.done || 0
            const total = s.total || form.count
            topicGeneration.progressPct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
            if (s.status === 'completed') {
              topicGeneration.running = false
              topicGeneration.progressPct = 100
              let items = s.result?.items || []
              if (!items.length && (s.result?.total_generated || 0) > 0) {
                items = await fetchRecentGeneratedTopics()
              }
              topicDrafts.value = items.map(normalizeTopicItem)
              ElMessage.success(`话题池生成完成，共 ${topicDrafts.value.length} 条，可继续编辑确认`)
            } else if (s.status === 'failed') {
              topicGeneration.running = false
              ElMessage.error('话题池生成失败：' + (s.error || ''))
            } else {
              topicGeneration.pollTimer = setTimeout(poll, 2000)
            }
          } catch (e) {
            topicGeneration.running = false
            ElMessage.error('获取话题池生成进度失败：' + (e.message || ''))
          }
        }
        poll()
      } catch (e) {
        topicGeneration.running = false
        ElMessage.error('启动话题池生成失败：' + (e.response?.data?.detail || e.message))
      }
    }

    const addTopicDraft = () => {
      topicDrafts.value.push(normalizeTopicItem({
        article_type: form.article_types[topicDrafts.value.length % Math.max(form.article_types.length, 1)],
        topic_text: currentThemeIp.value || currentDirection.value
          ? `${currentThemeIp.value ? `围绕${currentThemeIp.value}` : ''}${currentDirection.value ? `的${currentDirection.value}` : ''}`
          : '',
        search_query: [currentThemeIp.value, currentDirection.value].filter(Boolean).join(' '),
        materials: [],
        source: 'manual',
      }, topicDrafts.value.length))
    }

    const duplicateTopicDraft = (index) => {
      const row = topicDrafts.value[index]
      if (!row) return
      topicDrafts.value.splice(index + 1, 0, normalizeTopicItem({
        ...row,
        id: undefined,
        _key: undefined,
        source: 'manual',
      }, index + 1))
    }

    const removeTopicDraft = (index) => {
      topicDrafts.value.splice(index, 1)
    }

    const clearTopicDrafts = () => {
      topicDrafts.value = []
      resetTopicGenerationState()
    }

    const platformForIndex = (index) => form.platforms[index % Math.max(form.platforms.length, 1)]

    const jobsForBatchApi = () => topicDrafts.value
      .filter((j) => (j.topic_text || '').trim())
      .map((j, index) => {
        const base = {
          topic_tag: '',
          topic_name: (j.topic_text || '').trim(),
          article_type: j.article_type,
          platform: platformForIndex(index),
          prompt_type: 'author',
          angle_seed: 0,
          market: form.market,
          ip_code: form.ip_code || undefined,
          ip_name: currentThemeIp.value || undefined,
        }
        if (j.id != null) {
          base.topic_pool_id = j.id
          base.materials = normalizeMaterials(j.materials)
        }
        return base
      })

    const submitTask = async () => {
      if (!form.name) { ElMessage.warning('请输入任务名称'); return }
      if (!form.platforms.length) { ElMessage.warning('请至少选择一个平台'); return }
      if (!form.article_types.length) { ElMessage.warning('请至少选择一种文章类型'); return }
      if (!topicDrafts.value.length) { ElMessage.warning('请先生成或新增本批话题池'); return }
      const finalJobs = jobsForBatchApi()
      if (!finalJobs.length) { ElMessage.warning('请至少保留一条有效话题'); return }

      loading.value = true
      try {
        const res = await axios.post('/api/generate/batch', {
          name: form.name,
          jobs: finalJobs,
          max_concurrency: form.concurrency,
          generation_concurrency: form.concurrency,
        })
        const usedIds = topicDrafts.value.map((x) => x.id).filter((x) => x != null)
        await Promise.allSettled(usedIds.map((id) => topicPoolApi.resetStatus(id, 'used')))
        try {
          await formHistoryApi.save({ label: form.name, form: formSnapshot() })
          await loadFormHistory()
        } catch (e) {}
        ElMessage.success(`正文生成任务创建成功，ID: ${res.data?.task_id || res.task_id}，共 ${finalJobs.length} 条`)
      } catch (e) {
        ElMessage.error('创建正文生成任务失败: ' + (e.response?.data?.detail || e.message))
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.name = ''
      form.count = 10
      form.market = 'domestic'
      form.platforms = ['baijiahao']
      form.article_types = ['T-13']
      form.concurrency = 100
      form.direction = ''
      form.ip_code = ''
      form.ip_name = ''
      form.custom_ip = ''
      form.search_materials = true
      form.material_budget = 5
      topicDrafts.value = []
      resetTopicGenerationState()
      applyAutoTaskName(true)
    }

    onMounted(async () => {
      await Promise.all([loadConfig(), loadFormHistory(), loadGenreOptions(), loadIpOptions()])
      applyAutoTaskName(true)
    })

    onBeforeUnmount(() => {
      if (topicGeneration.pollTimer) clearTimeout(topicGeneration.pollTimer)
    })

    const allArticleTypes = articleTypeOptions

    return {
      form,
      loading,
      allArticleTypes,
      articleTypeOptions,
      platformOptions,
      genreOptions,
      ipOptions,
      ipLoading,
      topicDrafts,
      topicGeneration,
      onMarketChange,
      formHistoryList,
      selectedHistoryId,
      historyLabel,
      applyFormHistory,
      saveFormHistoryManual,
      getTypeSuffix,
      getTypeName,
      platformName,
      autoNamePreview,
      searchIps,
      onIpSelect,
      onTopicSeedChange,
      generateTopicPoolForBatch,
      addTopicDraft,
      duplicateTopicDraft,
      removeTopicDraft,
      clearTopicDrafts,
      normalizeMaterials,
      platformForIndex,
      submitTask,
      resetForm,
    }
  }
}
</script>

<style scoped>
.generation { padding: 0; }

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.preview-card { margin-top: 20px; }

.preview-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-note {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
  max-width: 680px;
}

.field-tip,
.type-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.inline-extra-input { margin-top: 8px; }

.suffix-preview {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.type-detail-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #606266;
}

.type-detail-name {
  font-weight: 500;
  color: #303133;
}

.type-detail-suffix {
  color: #909399;
  font-size: 11px;
}

.type-detail-suffix code {
  background: #f5f7fa;
  padding: 2px 5px;
  border-radius: 3px;
  color: #e6a23c;
  font-size: 11px;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-id { font-weight: 600; color: #409eff; min-width: 38px; }
.type-name { color: #303133; }
.type-tone { font-size: 11px; color: #909399; margin-left: auto; }

.count-hint { margin-left: 12px; font-size: 12px; color: #909399; }
.hint-text { margin-left: 10px; font-size: 12px; color: #909399; }

.topic-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.topic-progress {
  margin-top: 10px;
  width: 100%;
}

.topic-progress-msg {
  margin: 6px 0 0;
  font-size: 12px;
  color: #606266;
}
</style>
